import {
  Root as PopRoot,
  Trigger as PopTrigger,
  Content as PopContent,
  Portal as PopPortal,
} from '@radix-ui/react-popover';

import { Icon as MDIicon } from '@mdi/react';
import { mdiMenuDown } from '@mdi/js';

import { useState } from 'react';
import { styled, keyframes } from 'styled-components';
import { Truncate } from 'components/ui';
import DashboardList from './DashboardList';
import EditDashboard from './EditDashboard';
import DeleteDashboard from './DeleteDashboard';

const slideDownAndFade = keyframes`
  from {
    opacity: 0;
    transform: translateY(-2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Chevron = styled(MDIicon).attrs({ path: mdiMenuDown })`
  color: #fff;
  width: 20px;
  height: 20px;
  padding: 3px;
  position: absolute;
  right: 0;
`;

const Trigger = styled(PopTrigger)`
  border: none;
  border-radius: 2px;
  background-color: #273d56;
  display: flex;
  flex-direction: column;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  justify-content: center;
  height: 40px;
  max-width: 300px;
  text-align: left;
  padding: 5px 30px 5px 10px;
  position: relative;

  &:hover {
    background-color: #47637e;
    cursor: pointer;
  }
`;

const InProject = styled(Truncate)`
  color: #c2cad9;
  font-size: 10px;
  font-weight: 400;
`;

const Content = styled(PopContent)`
  background: #fff;
  animation: 400ms ${slideDownAndFade} cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;
`;

/**
 * @param {object} props -
 * @param {string} props.activeId - current selected dashboard id
 * @param {string} props.activeName - current selected dashboard name
 * @param {string} props.activeProject - current selected project
 * @param {string} props.orgKey - organization key
 * @param {Array} props.projects - array of projects
 */
export default function DashboardMenu({
  activeId,
  activeName,
  activeProject,
  orgKey,
  projects,
}) {
  const [open, setOpen] = useState(false);
  const [editDashboard, setEditDashboard] = useState(null);
  const [deleteDashboard, setDeleteDashboard] = useState(null);
  const EMPTY_MESSAGE =
    projects?.length > 0
      ? 'Select a dashboard'
      : 'No dashboards in organization';

  return (
    <>
      <PopRoot open={open} onOpenChange={setOpen}>
        <Trigger>
          {activeName ? <Truncate>{activeName}</Truncate> : EMPTY_MESSAGE}
          {activeProject && (
            <InProject>in project &ldquo;{activeProject}&rdquo;</InProject>
          )}
          <Chevron />
        </Trigger>
        <PopPortal>
          <Content align="start" sideOffset={5}>
            <DashboardList
              open={open}
              setOpen={setOpen}
              projects={projects}
              orgKey={orgKey}
              activeId={activeId}
              activeProject={activeProject}
              onClickEdit={setEditDashboard}
              onClickDelete={setDeleteDashboard}
            />
          </Content>
        </PopPortal>
      </PopRoot>

      <EditDashboard
        onClose={() => setEditDashboard(null)}
        dashboard={editDashboard}
      />

      <DeleteDashboard
        onClose={() => setDeleteDashboard(null)}
        dashboard={deleteDashboard}
      />
    </>
  );
}
